import countriesLocales from '@shared-core/locales/countries.dynamic.i18n.json'
import type { CountryCode } from '@shared-core/utils/phone'
import { flags } from '@shared-images/flags'

export type Country = {
  code: CountryCode
  name: string
  flagKey: keyof typeof flags
}

export type CountryMeta = Pick<Country, 'code' | 'name' | 'flagKey'>

export function isCountryWithFlag(code: string): code is CountryCode & keyof typeof flags {
  return code in flags
}

export function getCountryMetaByCode(countries: Country[], code?: string): CountryMeta | null {
  if (!code) return null
  return countries.find((country) => country.code === code) ?? null
}

function findFlagKeyInCountries(countries: Country[], name: string): Country['flagKey'] | null {
  const exactMatch = countries.find((item) => item.name.toLowerCase() === name)

  if (exactMatch) return exactMatch.flagKey

  const partialMatch = countries.find(
    (item) => item.name.toLowerCase().includes(name) || name.includes(item.name.toLowerCase()),
  )

  return partialMatch?.flagKey ?? null
}

function findFlagKeyInEnglishLocales(name: string): Country['flagKey'] | null {
  const englishCountries = countriesLocales.en

  if (!englishCountries) return null

  const englishMatch = Object.entries(englishCountries).find(([key, label]) => {
    if (!key.startsWith('country-')) return false

    const labelLower = label.toLowerCase()

    return labelLower === name || labelLower.includes(name) || name.includes(labelLower)
  })

  if (!englishMatch) return null

  const englishCountryCode = englishMatch[0].replace('country-', '')

  return isCountryWithFlag(englishCountryCode) ? englishCountryCode : null
}

export function getCountryFlagKey(
  countries: Country[],
  country?: string,
  countryCode?: string,
): Country['flagKey'] | null {
  const normalizedCountryCode = countryCode?.trim().toLowerCase()

  if (normalizedCountryCode && isCountryWithFlag(normalizedCountryCode)) return normalizedCountryCode

  const name = country?.trim().toLowerCase()

  if (!name) return null

  if (isCountryWithFlag(name)) return name

  return findFlagKeyInCountries(countries, name) ?? findFlagKeyInEnglishLocales(name)
}

export function getCountryMetaWithFallback(
  countries: Country[],
  code?: string,
  fallbackName?: string,
): CountryMeta | null {
  if (!code) return null
  const existing = getCountryMetaByCode(countries, code)

  if (existing) return existing

  if (!isCountryWithFlag(code)) return null

  return {
    code,
    name: fallbackName ?? code.toUpperCase(),
    flagKey: code,
  }
}
